import { Box, Button, CircularProgress } from '@mui/material';
import { usePost } from 'hooks/use-post';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import _ from 'underscore';

import Collapsible from 'components/collapsible';
import LoadingSpinner from 'components/loading-spinner';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import RegistrationHeader from 'views/production-systems/components/registration-header';
import ListAerators from 'views/production-systems/features/farm-setup/components/list-aerators';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { AeratorsProvider } from 'views/self-service/features/aerators/context/aerators-context';

import { putAerators } from 'views/production-systems/features/farm-setup/api/put-aerators';
import { Aerator, PondAeratorAssignments, PondsWithAerators } from 'views/production-systems/features/farm-setup/types/aerators.type';
import { Pond } from 'views/production-systems/features/farm-setup/types/pond.type';
import {
  PondAeratorAssigments,
  UpdateAeratorInputForm,
  UpdateAeratorsInput,
  UpdateAssignedAerators,
  UpdateAssignedAeratorsInput,
} from 'views/production-systems/features/farm-setup/types/update-aerators-input.type';

import { postPondStatusAerators } from 'views/production-systems/features/farm-setup/api/get-pond-aerator-status';

type Props = {
  ponds: Pond[];
  handleClose: () => void;
};

type ParamTypes = {
  globalSiteId: string;
};

const ManageAerators: React.FC<Props> = ({ ponds, handleClose }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const { lockedPonds, registrationSetupDate: registrationDate } = usePondsState();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [pondAeratorAssignments, setPondsAeratorsAssignments] = useState<PondAeratorAssignments[]>();

  // #region HOOKS
  const pondsWithAerators = useMemo(() => {
    return _.chain(pondAeratorAssignments)
      .sortBy('pondName')
      .value()
      .map((pond) => {
        return {
          pondId: pond.pondId,
          pondName: pond.pondName,
          pondCurrentBiomass: pond.pondCurrentBiomass,
          assignedAerators: pond.assignedAerators
            ?.filter((aerator) => aerator.aeratorId)
            .map((aerator) => {
              return {
                numberOfAerators: aerator.numberOfAerators,
                aeratorId: aerator.aeratorId,
                aeratorName: aerator.aeratorName,
                aeratorBrand: aerator.aeratorBrand,
                horsepower: aerator.horsepower,
                supportedBiomass: aerator.supportedBiomass,
                aeratorTypeId: aerator.aeratorTypeId,
                edit: false,
                isDeleted: aerator.numberOfAerators === 0,
              };
            }),
        };
      }) as UpdateAeratorsInput[];
  }, [ponds, pondAeratorAssignments]);
  // #endregion

  // #region FORMS
  const defaultValues = {
    ponds: pondsWithAerators,
  };

  const form = useForm<UpdateAeratorInputForm>({
    defaultValues: defaultValues,
    shouldUnregister: false,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  const { fields: rows } = useFieldArray({
    control: control,
    name: `ponds`,
  });

  const watchPonds = useWatch({ control, name: 'ponds' }) || [];

  // #endregion

  // #region POST DATA
  const { isPending: isPondsLoading, mutate: getPondsWithAssignedAerators } = usePost<
    PondsWithAerators,
    { globalSiteId: string; statusDate: string; ponds: string[] }
  >(
    (request) => postPondStatusAerators(request.globalSiteId, request.statusDate, request.ponds),
    (data) => {
      if (data) {
        const pondAeratorAssignments = data.pondAeratorAssignments.map((pond) => pond);
        setPondsAeratorsAssignments(pondAeratorAssignments);
      }
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const { isPending: isAssignLoading, mutate: assignAeratorsToPonds } = usePost<
    Aerator,
    { globalSiteId: string; request: UpdateAssignedAeratorsInput }
  >(
    (request) => putAerators(request.globalSiteId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      handleClose();
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  // #region HANDLERS
  const handleSubmitAeratorsData = (request: { ponds: UpdateAeratorsInput[] }) => {
    const assignments: PondAeratorAssigments[] = request.ponds
      .map((pond) => {
        const aeratorsForPond = pond.assignedAerators.reduce((acc: UpdateAssignedAerators[], aerator) => {
          if (!aerator.isDeleted) {
            acc.push({
              aeratorId: aerator.aeratorId,
              quantity: Number(aerator.numberOfAerators),
            });
          } else if (aerator.isDeleted && !aerator.isNew) {
            acc.push({
              aeratorId: aerator.aeratorId,
              quantity: 0,
            });
          }
          return acc;
        }, []);

        return {
          pondId: pond.pondId,
          assignedAerators: aeratorsForPond,
        };
      })
      .filter((pond) => pond.assignedAerators.length > 0)
      .map((pond) => {
        return {
          pondId: pond.pondId,
          assignedAerators: pond.assignedAerators.map((aerator) => ({
            aeratorId: aerator.aeratorId,
            quantity: Number(aerator.quantity),
          })),
        } as PondAeratorAssigments;
      });

    const pondsWithAssignments: UpdateAssignedAeratorsInput = {
      date: registrationDate.format('YYYY-MM-DD'),
      pondAeratorAssignments: assignments,
    };

    if (pondsWithAssignments.pondAeratorAssignments.length === 0) return;

    assignAeratorsToPonds({ globalSiteId: params.globalSiteId, request: pondsWithAssignments });
  };

  const handleGetPondsData = (globalSiteId: string, statusDate: string) => {
    const request = {
      globalSiteId: globalSiteId,
      statusDate: statusDate,
      ponds: ponds.map((pond) => pond.pondId),
    };
    getPondsWithAssignedAerators(request);
  };

  const isDisableSubmit = watchPonds
    .flatMap((pond) => pond.assignedAerators || [])
    .every((aerator) => aerator.isNew && aerator.isDeleted);

  // #endregion

  useEffect(() => {
    const statusDate = registrationDate.format('YYYY-MM-DD').toString();
    handleGetPondsData(params.globalSiteId, statusDate);
  }, []);

  useEffect(() => {
    reset({ ponds: pondsWithAerators });
  }, [pondsWithAerators]);

  return isPondsLoading ? (
    <LoadingSpinner />
  ) : lockedPonds.notGranted.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <>
        <PondLockTimer handleDialogClose={handleClose} />
        <RegistrationHeader dateCreated={registrationDate} />
        <FormProvider {...form}>
          <form onSubmit={handleSubmit((data) => handleSubmitAeratorsData(data))}>
            <Box display={'flex'} flexDirection={'column'} gap={4}>
              {_.chain(rows)
                .sortBy('pondName')
                .value()
                .map((pond, idx) => {
                  const { pondCurrentBiomass, pondName } = pond;
                  return (
                    <Collapsible hideHeaderBorder label={`${pondName}`} key={idx} open={idx === 0}>
                      <ListAerators fieldIndex={idx} pondCurrentBiomass={pondCurrentBiomass} />
                    </Collapsible>
                  );
                })}
              <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
                <Button
                  type="submit"
                  disabled={isAssignLoading || !isValid || !isDirty || isDisableSubmit}
                  startIcon={isAssignLoading ? <CircularProgress color="inherit" size={18} /> : null}>
                  <FormattedMessage id="general.submit" values={{ type: '' }} />
                </Button>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </>
    </Box>
  );
};

const ManageAeratorsContainer: React.FC<Props> = ({ ponds, handleClose }) => {
  return (
    <AeratorsProvider>
      <ManageAerators ponds={ponds} handleClose={handleClose} />
    </AeratorsProvider>
  );
};

export default React.memo(ManageAeratorsContainer);
